import { ArrowRight, CheckCircle2, Megaphone, TrendingUp, ShoppingCart, Headphones } from "lucide-react";
import { Link } from "react-router-dom";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/hooks/useLanguage";

const agentMeta = [
  {
    icon: Megaphone,
    iconColor: "text-chathero-mint",
    iconBg: "bg-chathero-mint/20",
    iconBorder: "border-chathero-mint/50",
    checkColor: "text-chathero-mint",
    linkColor: "text-chathero-mint",
    link: "/loesungen/marketing",
  },
  {
    icon: TrendingUp,
    iconColor: "text-chathero-orange",
    iconBg: "bg-chathero-orange/20",
    iconBorder: "border-chathero-orange/50",
    checkColor: "text-chathero-orange",
    linkColor: "text-chathero-orange",
    link: "/loesungen/vertrieb",
  },
  {
    icon: ShoppingCart,
    iconColor: "text-chathero-orange",
    iconBg: "bg-chathero-orange/20",
    iconBorder: "border-chathero-orange/50",
    checkColor: "text-chathero-orange",
    linkColor: "text-chathero-orange",
    link: "/loesungen/ecommerce",
  },
  {
    icon: Headphones,
    iconColor: "text-chathero-mint",
    iconBg: "bg-chathero-mint/20",
    iconBorder: "border-chathero-mint/50",
    checkColor: "text-chathero-mint",
    linkColor: "text-chathero-mint",
    link: "/loesungen/kundenservice",
  },
] as const;

const AIAgentsDetailSection = () => {
  const { ref, isVisible } = useScrollAnimation();
  const { t, tObject, localizePath } = useLanguage();
  const agents = tObject<Array<{ name: string; tagline: string; description: string; functions: string[]; linkText: string }>>("aiAgent.detail.agents");

  return (
    <section
      ref={ref as React.RefObject<HTMLElement>}
      className={cn(
        "py-16 md:py-24 bg-background transition-all duration-700 ease-out",
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      )}
    >
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-6">
            <Badge variant="outline" className="mb-4 text-sm font-semibold text-primary uppercase tracking-wider">
              {t("aiAgent.detail.badge")}
            </Badge>

            <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-6">
              {t("aiAgent.detail.title")}
            </h2>
          </div>

          <p className="text-lg text-muted-foreground text-center mb-12 max-w-3xl mx-auto">
            {t("aiAgent.detail.intro")}
          </p>

          <div className="grid lg:grid-cols-2 gap-8">
            {agents.map((agent, index) => {
              const meta = agentMeta[index];
              return (
                <div
                  key={agent.name}
                  className={cn(
                    "bg-background rounded-2xl p-8 border border-border shadow-[0_4px_24px_rgba(0,196,180,0.08)] hover:-translate-y-1 hover:shadow-[0_8px_32px_rgba(0,196,180,0.14)] transition-all duration-300 group",
                    isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
                  )}
                  style={{
                    transitionDelay: isVisible ? `${index * 150}ms` : "0ms",
                    transitionDuration: "500ms",
                  }}
                >
                  <div className="flex items-start gap-5 mb-5">
                    <div className={`w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0 ${meta.iconBg} border-2 ${meta.iconBorder} group-hover:scale-110 transition-transform duration-300`}>
                      <meta.icon className={`w-8 h-8 ${meta.iconColor}`} />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-primary mb-1">{agent.name}</h3>
                      <p className={`${meta.linkColor} font-semibold text-sm`}>{agent.tagline}</p>
                    </div>
                  </div>

                  <p className="text-muted-foreground leading-relaxed mb-5">{agent.description}</p>

                  <div className="mb-5">
                    <p className="text-sm font-bold text-primary mb-2">{t("aiAgent.detail.functionsLabel")}</p>
                    <div className="w-8 h-0.5 bg-border mb-3" />
                    <ul className="space-y-2.5">
                      {agent.functions.map((func, funcIndex) => (
                        <li key={funcIndex} className="flex items-start gap-2.5">
                          <CheckCircle2 className={`w-[18px] h-[18px] ${meta.checkColor} flex-shrink-0 mt-0.5`} />
                          <span className="text-[15px] text-muted-foreground">{func}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="pt-4 border-t border-border">
                    <Link
                      to={localizePath(meta.link)}
                      className={`inline-flex items-center gap-2 text-sm ${meta.linkColor} font-semibold group-hover:gap-3 transition-all`}
                    >
                      {agent.linkText}
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AIAgentsDetailSection;
